import React, { PropsWithChildren, ReactNode } from "react";
import styled, { css } from "styled-components";
import { assertNever } from "../amorg-common";
import { colors } from "./colors/color";
import { Icon, iconName } from "./icons/Icons";
import { getIndexColors, shadeOrTintRGBHexColor } from "./colors/colorHelper";
import { colorGenerator } from './colors/colorGenerator';
import { TextAvatar } from "./avatar/TextAvatar";
import { Flex } from "./compositeComponents/FlexHelpers";


export function ChipPanel(props:{children:ReactNode, style?:React.CSSProperties}):JSX.Element
{
    return (
        <Flex gap={4} style={{ flexWrap: 'wrap', alignItems:'center', ...props.style }}>
            {props.children}
        </Flex>
    )
}



/*******************************************************************
 * 
 * no icons - leading icon is reserved for check if selected
 * selected uses 'filled' variant unless variantOverride is provided
 * not-selected is outlined
 * 
 ******************************************************************/

type ActionChipProps = 
    ChipConfig & {
    leadingIcon?:iconName,
    trailingIcon?:iconName,
    avatar?: ChipAvatarContent
    disabled?:boolean,
    onClick?:()=>void,
};

export function ActionChip(props:PropsWithChildren<ActionChipProps>):JSX.Element
{
    let internalChipStyle = getInternalChipStyle(props);

    if(props.disabled)
    {
        internalChipStyle = getVariantStyle('disabled');
        internalChipStyle.hoverBackgroundAndBorderColor = undefined;
    }

    return (
        <InternalChip
            {...internalChipStyle}
            leadingIcon={props.leadingIcon}
            trailingIcon={props.trailingIcon}
            avatar={props.avatar}
            onClick={() => props.onClick && !props.disabled && props.onClick()}
        >
            {props.children}
        </InternalChip>
    )
}


/*******************************************************************
 * 
 * no icons - leading icon is reserved for check if selected
 * selected uses 'filled' variant unless variantOverride is provided
 * not-selected is outlined
 * 
 ******************************************************************/

type ToggleChipProps = {
    selected:boolean,
    selectedStyle?:ChipConfig,
    avatar?: ChipAvatarContent
    disabled?:boolean,
    onClick?:(selected:boolean)=>void,
}
export function ToggleChip(props:PropsWithChildren<ToggleChipProps>):JSX.Element
{
    let internalChipStyle = getVariantStyle('outlined');
    if(props.selected)
    {
        internalChipStyle = getVariantStyle('filled-light');
        if(props.selectedStyle)
            internalChipStyle = getInternalChipStyle(props.selectedStyle);

        //No hover
        if(props.disabled || props.onClick === undefined)
            internalChipStyle.hoverBackgroundAndBorderColor = undefined;
    }
    else {
        internalChipStyle = getVariantStyle('outlined');
        
        //No hover
        if(props.disabled)
            internalChipStyle = getVariantStyle('disabled');
    }

    return (
        <InternalChip
            {...internalChipStyle}
            avatar={props.avatar}
            leadingIcon={props.selected ? 'actions_done' : undefined}
            reserveIconSpace
            disabled={props.disabled}
            onClick={() => props.onClick && props.onClick(!props.selected)}
        >
            {props.children}
        </InternalChip>
    )
}


/*******************************************************************
 * 
 * No hover
 * Uses filled variant style if no color is provided via variant or variantOverride
 * 
 ******************************************************************/
type InfoChipProps = 
    ChipConfig & {
    avatar?: ChipAvatarContent,
    leadingIcon?:iconName,
    trailingIcon?:iconName
}
export function InfoChip(props:PropsWithChildren<InfoChipProps>):JSX.Element
{
    const internalChipStyle = getInternalChipStyle(props);
    // No hover for info chip
    internalChipStyle.hoverBackgroundAndBorderColor = undefined;

    return (
        <InternalChip
            {...internalChipStyle}
            
            avatar={props.avatar}
            leadingIcon={props.leadingIcon}
            trailingIcon={props.trailingIcon}
        >
            {props.children}
        </InternalChip>
    )
}

/********************************************************************
 * 
 * Configuration types for all exported chips
 * 
 ********************************************************************/

export type ChipAvatarContent = {
    text:string,
    variant?: 'outlined' | 'filled' | 'warning' | 'dimmed'
}

export type ChipVariant = 'outlined' | 'outlined-blue' | 'filled' | 'filled-light' | 'filled-dark' | 'warning' | 'error' | 'disabled';
export type ChipConfig = { 
    variant?:ChipVariant,

    colorIndex?:number,
    colorHash?:string
};

/********************************************************************
 * 
 * Base chip component that is used by all other chip components. Not meant to be used directly.
 * 
 ********************************************************************/

type InternalChipStyle = {
    backgroundColor:string,
    contentColor:string,
    borderColor:string
    hoverBackgroundAndBorderColor?:string
}

type InternalChipProps =
    InternalChipStyle & {
    avatar?:ChipAvatarContent,
    leadingIcon?:iconName,
    trailingIcon?:iconName,
    reserveIconSpace?:boolean,
    disabled?:boolean,
    onClick?:()=>void
}
export function InternalChip(props:PropsWithChildren<InternalChipProps>): JSX.Element
{
    const styling:InternalChipStyle = {
        backgroundColor: props.backgroundColor,
        contentColor: props.contentColor,
        borderColor: props.borderColor,
        hoverBackgroundAndBorderColor: props.hoverBackgroundAndBorderColor
    };

    let avatarColors:{backgroundColor:string, contentColor:string} | undefined;
    if(props.avatar)
        avatarColors = getAvatarColors(props.avatar);

    const reservedIconWidth = '16px';
    const internalGap = '4px';

    let reserveTrailingIconSpace:boolean = props.reserveIconSpace || false;
    if(props.reserveIconSpace && props.avatar)
    {
        // If we have an avatar, we don't need to reserve space for a trailing icon, since the avatar will be there instead
        reserveTrailingIconSpace = false;
    }

    const handleOnClick = props.disabled ? undefined : props.onClick;

    return (
        <PillContainer 
            role={props.onClick ? 'button' : undefined}
            tabIndex={props.onClick ? 0 : undefined}
            
            onClick={handleOnClick}

            {...styling}
        >
            <>
            
                {(props.leadingIcon || props.reserveIconSpace) &&
                    <div style={{ width:reservedIconWidth, height:reservedIconWidth, marginRight: internalGap }}>
                        {props.leadingIcon &&
                            <Icon name={props.leadingIcon} size="x-small" />
                        }
                    </div>
                }

                <div>{props.children}</div>

                {(props.trailingIcon || reserveTrailingIconSpace) &&
                    <div style={{ width:reservedIconWidth, height:reservedIconWidth, marginLeft: internalGap }}>
                        {props.trailingIcon &&
                            <Icon name={props.trailingIcon} size="x-small" />
                        }
                    </div>
                }

                {props.avatar &&
                    <>
                        <div style={{ marginLeft: internalGap }}>
                            <TextAvatar text={props.avatar.text} size="16px" backgroundColor={avatarColors?.backgroundColor} contentColor={avatarColors?.contentColor} />
                        </div>

                        {/* If space was reserved for a leading icon and an avar is displayed, we need space to the right of the avatar as wel */}
                        {props.reserveIconSpace &&
                            <div style={{ width:reservedIconWidth, height:reservedIconWidth, marginLeft: internalGap }}></div>
                        }
                    </>
                }
            </>
        </PillContainer>
    );
}


const hoverTransition = 'transition: all 0.5s ease';

const PillContainer = styled.div<InternalChipStyle>`
    display:inline-flex;
    min-width: 40px;
    min-height: 26px;
    justify-content: center;
    align-items: center;
    padding: 4px 8px;
    border-radius: 14px;
       
    font-family: "Noto Sans", sans-serif;
    font-size: 14px;
    line-height: 14px;
    font-weight: 400;

    color: ${props => props.contentColor};
    & svg { 
        fill: ${props => props.contentColor};
    }

    background: ${props => props.backgroundColor};
    ${props => props.hoverBackgroundAndBorderColor && css`
        &:hover {
            ${hoverTransition};
            background: ${props.hoverBackgroundAndBorderColor};
        }
    `};

    border: ${props => `1px solid ${props.borderColor}`};
    ${props => props.hoverBackgroundAndBorderColor && css`
        &:hover {
            ${hoverTransition};
            border: ${`1px solid ${props.hoverBackgroundAndBorderColor}`};
        }
    `};

    cursor: default;
    ${props => props.hoverBackgroundAndBorderColor && css`
        cursor: pointer;
    `};
`;

function getInternalChipStyle(config:ChipConfig):InternalChipStyle
{
    // variant is first priority
    if(config.variant)
        return getVariantStyle(config.variant);

    // colorIndex is second priority
    if(config.colorIndex !== undefined)
        return getIndexedStyle(config.colorIndex);

    // colorHash is third priority
    if(config.colorHash)
        return getColorGenerationStyle(config.colorHash);

    // default to filled variant if no other config is provided
    return getVariantStyle('outlined');
}

function getVariantStyle(variant:ChipVariant):InternalChipStyle
{
    // spread into a new object so colors.chip.styles.* is never mutated by callers
    if(variant === 'filled') return { ...colors.chip.styles.filled };
    else if(variant === 'filled-light') return { ...colors.chip.styles.filledLight };
    else if(variant === 'filled-dark') return { ...colors.chip.styles.filledDark };
    else if(variant === 'outlined') return { ...colors.chip.styles.outlined };
    else if(variant === 'outlined-blue') return { ...colors.chip.styles.outlinedBlue };
    else if(variant === 'warning') return { ...colors.chip.styles.warning };
    else if(variant === 'error') return { ...colors.chip.styles.error };
    else if(variant === 'disabled') return { ...colors.chip.styles.disabled };
    else assertNever(variant);
}

function getIndexedStyle(index:number/*, content?: 'light' | 'dark'*/):InternalChipStyle
{
    const color = getIndexColors(index).dark

    return {
        backgroundColor: color,
        contentColor: colors.text.white,
        borderColor: color,
        // light background - darken on hover
        hoverBackgroundAndBorderColor: shadeOrTintRGBHexColor(color, -40)
    };
}

function getColorGenerationStyle(colorHash:string):InternalChipStyle
{
    const hash = colorHash || 'default';

    const darkColor = colorGenerator.getDarkColorFromString(hash);
    return {
        backgroundColor: darkColor,
        contentColor: colors.text.white,
        borderColor: darkColor,
        hoverBackgroundAndBorderColor: shadeOrTintRGBHexColor(darkColor, 20)
    };
}

function getAvatarColors(avatar:ChipAvatarContent):{backgroundColor:string, contentColor:string}
{
    const variant = avatar.variant || 'filled';

    if(variant === 'filled')
        return { ...colors.chip.avatar.styles.filled };
    else if(variant === 'outlined')
        return { ...colors.chip.avatar.styles.outlined };
    else if(variant === 'warning')
        return { ...colors.chip.avatar.styles.warning };
    else if(variant === 'dimmed')
        return { ...colors.chip.avatar.styles.dimmed };
    else assertNever(variant);
}
